import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import PageLayout from "@/components/layout/PageLayout";
import { Card } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { FileText } from "lucide-react";
import { Database } from "@/integrations/supabase/types";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";

type PolicyWithProfile = Database["public"]["Tables"]["policies"]["Row"] & {
  profiles: {
    first_name: string | null;
    last_name: string | null;
  } | null;
};

const AdminIdCardsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [policies, setPolicies] = useState<PolicyWithProfile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminAndFetchPolicies = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate("/auth");
        return;
      }

      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .single();

      if (!roles || roles.role !== "admin") {
        toast({
          variant: "destructive",
          title: "Access Denied",
          description: "You don't have permission to access this page.",
        });
        navigate("/dashboard");
        return;
      }

      const { data, error } = await supabase
        .from("policies")
        .select(`
          *,
          profiles:profiles (
            first_name,
            last_name
          )
        `)
        .eq("status", "active") as { data: PolicyWithProfile[] | null; error: any };

      if (error) {
        toast({
          variant: "destructive",
          title: "Error fetching policies",
          description: error.message,
        });
        return;
      }

      setPolicies(data || []);
      setLoading(false);
    };

    checkAdminAndFetchPolicies();
  }, [navigate, toast]);

  if (loading) {
    return (
      <PageLayout>
        <div className="container mx-auto py-8">
          <p>Loading...</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <SidebarProvider>
        <div className="flex min-h-screen w-full">
          <DashboardSidebar />
          <div className="flex-1">
            <div className="container mx-auto py-8">
              <h1 className="text-2xl font-bold mb-6">ID Cards</h1>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {policies.map((policy) => (
                  <Card key={policy.id} className="p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold mb-2">
                          {policy.insurance_type} Insurance
                        </h3>
                        <div className="space-y-2 text-sm">
                          <p>Policy Number: {policy.policy_number}</p>
                          <p>User: {policy.profiles?.first_name} {policy.profiles?.last_name}</p>
                          <p>Valid until: {new Date(policy.end_date).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <button 
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        onClick={() => {
                          toast({
                            title: "Download started",
                            description: "The ID card is being downloaded.",
                          });
                        }}
                      >
                        <FileText className="h-5 w-5 text-gray-600" />
                      </button>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </SidebarProvider>
    </PageLayout>
  );
};

export default AdminIdCardsPage;
